import { useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { PeopleFormData } from '../../../../Types/AdvanceSearchResultsTypes';
import { actions } from '../../../../Slice/AdvanceSearchSlice';
import {
  selectAdvanceSearchMetadata,
  selectAdvanceSearchPeopleForm,
} from '../../../../Selector/AdvanceSearchSelector';
import {
  buildAdvanceSearchRegionOptions,
  buildAdvanceSearchCountryOptions,
  getAdvanceSearchRegionForCountry,
} from '../../../../Utils/UtilityFunctions';
import { FormField } from './FormField';

const FormContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

interface PeopleFormProps {
  isLoading?: boolean;
}

export const PeopleForm: React.FC<PeopleFormProps> = ({ isLoading }) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectAdvanceSearchMetadata);
  const formData = useSelector(selectAdvanceSearchPeopleForm);
  const regionOptions = useMemo(
    () => buildAdvanceSearchRegionOptions(filters?.regions),
    [filters?.regions],
  );
  const countryOptions = useMemo(
    () => buildAdvanceSearchCountryOptions(filters, formData.region),
    [filters, formData.region],
  );
  const countryId = useMemo(
    () => countryOptions.find((option) => option.label === formData.country)?.id ?? 0,
    [countryOptions, formData.country],
  );
  const regionId = useMemo(
    () => regionOptions.find((option) => option.label === formData.region)?.id ?? 0,
    [regionOptions, formData.region],
  );

  const updateFields = (partial: Partial<PeopleFormData>) => {
    dispatch(actions.setAdvanceSearchPeopleForm({ ...formData, ...partial }));
  };

  const updateField = (field: keyof PeopleFormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    dispatch(actions.setAdvanceSearchPeopleForm(newData));
  };

  const handleCountryChange = (id: number) => {
    if (!id) {
      updateFields({ country: '' });
      return;
    }
    const selectedLabel = countryOptions.find((option) => option.id === id)?.label ?? '';
    updateFields({ country: selectedLabel, region: getAdvanceSearchRegionForCountry(filters, selectedLabel) });
  };

  const handleRegionChange = (id: number) => {
    if (!id) {
      updateFields({ region: '' });
      return;
    }
    const selectedLabel = regionOptions.find((option) => option.id === id)?.label ?? '';
    if (selectedLabel === formData.region) return;
    updateFields({ region: selectedLabel, country: '' });
  };

  return (
    <FormContainer>
      <FormField
        label="Full Name"
        value={formData.fullName}
        onChange={(val) => updateField('fullName', val)}
        placeholder="Enter full name"
      />
      <FormField
        label="Current"
        value={formData.currentCo}
        onChange={(val) => updateField('currentCo', val)}
        placeholder="Current company name"
        sup="Company"
        isSupText={true}
      />
      <FormField
        label="Previous"
        value={formData.previousCo}
        onChange={(val) => updateField('previousCo', val)}
        placeholder="Previous company name"
        sup="Company"
        isSupText={true}
      />
      <FormField
        label="Job Title"
        value={formData.designation}
        onChange={(val) => updateField('designation', val)}
        placeholder="Enter your position"
      />
      <FormField
        label="Country"
        type="dropdown"
        value={countryId}
        onChange={(val) => handleCountryChange(val as number)}
        options={countryOptions}
        placeholder="Select country"
        isLoading={isLoading}
        hasSearch={true}
      />
      <FormField
        label="Region"
        type="dropdown"
        value={regionId}
        onChange={(val) => handleRegionChange(val as number)}
        options={regionOptions}
        placeholder="Select region"
        isLoading={isLoading}
        hasSearch={true}
      />
    </FormContainer>
  );
};